import React, {
  createRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import tw from "twin.macro"
import { CarCareSubNavProps } from "./CarCareSubNav.d"
import CarCareBreadcrumb from "../CarCareBreadcrumb/CarCareBreadcrumb"
import { Link } from "gatsby"
import { LanguageContext } from "../../../contexts/Language"
import CarCareBackButton from "../CarCareBackButton/CarCareBackButton"
import { detectMobileView, firstLettersCapital } from "../../../helpers"
import useTealiumEvent from "../../../hooks/Tealium/useTealiumEvent"
import Icon from "../../atoms/Icon"
import { blackUnderlineAnimation } from "../../../styles/animations"

const CarCareSubNav = ({
  menuName,
  menuItems,
  pathname,
  showBreadcrumb = false,
  showBackButton = false,
  fromTopicLanding = false,
  fromArticle = false,
  articleCategory = "",
}: CarCareSubNavProps) => {
  const { language } = useContext(LanguageContext)
  const [isMobile, setIsMobile] = useState<boolean>()
  const [showItems, setShowOptions] = useState(false)
  useEffect(() => {
    setIsMobile(detectMobileView())
  }, [])

  /* Animation styles applied for non-mobile devices */

  // Create an array of refs, one for each item
  const itemRefs = useRef(menuItems?.map(() => createRef<HTMLLIElement>()))
  itemRefs.current = menuItems?.map(
    (_, i) => itemRefs.current[i] ?? createRef(),
  )
  // Tealium
  const { trackTealEvent } = useTealiumEvent()

  return (
    <>
      {showBreadcrumb && (
        <div css={[tw`pt-5 pb-2 px-5 lg:(px-10 mx-auto max-w-[1440px])`]}>
          <CarCareBreadcrumb pathname={pathname} />
          <div css={[tw`border-b border-gray-400 mx-5 lg:(hidden)`]}></div>
        </div>
      )}
      {showBackButton && typeof window !== "undefined" && (
        <div css={[tw`flex mx-auto px-5 lg:(max-w-[1440px]) `]}>
          <CarCareBackButton pathname={window?.location?.pathname} />
        </div>
      )}
      {isMobile ? (
        <div
          css={[
            tw`w-full mx-auto flex flex-col justify-center text-center mt-10 text-lg`,
          ]}
        >
          <div>
            <button
              css={[
                tw`bg-wildSand min-w-[217px] rounded-md py-3 px-4 mx-auto font-semibold flex justify-between`,
              ]}
              onClick={() => setShowOptions(!showItems)}
            >
              {`${menuName}: ${firstLettersCapital(articleCategory)}`}
              <Icon.Chevron
                color="black"
                direction="down"
                css={[
                  tw`h-3 transition-all duration-300 mt-2.5 ml-2`,
                  showItems && tw`transform -rotate-180`,
                ]}
              />
            </button>
          </div>
          <ul css={[tw`flex-col gap-5`]}>
            {showItems &&
              menuItems?.map((item, index) => {
                const activePage =
                  pathname == `/${item?.titleLink?.slug?.current}/` ||
                  pathname == `/es/${item?.titleLink?.slug?.current}/`
                return (
                  <li
                    key={`${item?._key}`}
                    css={[tw`py-2 px-5 rounded-full flex items-center gap-2`]}
                    ref={itemRefs.current[index]}
                  >
                    <Link
                      to={
                        language == "es"
                          ? `/es/${item?.titleLink?.slug?.current}`
                          : `/${item?.titleLink?.slug?.current}`
                      }
                      state={{ scrollRestoration: false }}
                      css={[
                        activePage &&
                          tw`font-semibold pointer-events-none cursor-not-allowed`,
                        tw`mx-auto py-1 px-4  relative focus:(font-semibold text-toyotaRed)`,
                        blackUnderlineAnimation,
                      ]}
                      analytics-id={`topic:articles:${index + 1}`}
                      onClick={() => {
                        trackTealEvent({
                          tealium_event: fromArticle ? "Live" : "nav_click",
                          click_text: `topic subnav:${item?.title}`,
                          ...((fromArticle || fromTopicLanding) && {
                            vehicle_category: [articleCategory],
                          }),
                        })
                      }}
                    >
                      <span css={[activePage && tw`border-red-500 border-b-4`]}>
                        {item?.title}
                      </span>
                    </Link>
                  </li>
                )
              })}
          </ul>
        </div>
      ) : (
        <div
          css={[
            tw`hidden  md:(flex mx-5 pr-10 w-full overflow-scroll overflow-y-hidden scrollbar-hide) lg:(mx-auto)`,
          ]}
        >
          <div
            aria-label={menuName}
            css={[
              tw`overflow-visible py-5 px-5 rounded-full text-lg flex gap-x-2 bg-wildSand w-min whitespace-nowrap`,
              tw`md:(mt-10 px-10 py-10 gap-x-8)`,
              tw`lg:(px-20 mx-auto text-xl)`,
            ]}
          >
            <h2 css={[tw`flex`]}>{`${menuName}:`}</h2>
            <ul css={[tw`flex gap-x-2 md:(gap-x-8 )`]}>
              {menuItems?.map((item, index) => {
                const activePage =
                  pathname == `/${item?.titleLink?.slug?.current}/` ||
                  pathname == `/es/${item?.titleLink?.slug?.current}/`

                return (
                  <li
                    key={`${item?._key}`}
                    css={[tw`relative`]}
                    ref={itemRefs.current[index]}
                  >
                    <Link
                      to={
                        language == "es"
                          ? `/es/${item?.titleLink?.slug?.current}`
                          : `/${item?.titleLink?.slug?.current}`
                      }
                      state={{ scrollRestoration: false }}
                      css={[
                        activePage && [
                          tw`font-semibold pointer-events-none cursor-not-allowed bg-[#ecebeb] rounded-full`,
                        ],
                        tw` py-1 px-4  relative focus:(font-semibold text-toyotaRed)`,
                        blackUnderlineAnimation,
                      ]}
                      analytics-id={`topic:articles:${index + 1}`}
                      onClick={() => {
                        trackTealEvent({
                          tealium_event: fromArticle ? "Live" : "nav_click",
                          click_text: `topic subnav:${item?.title}`,
                          ...((fromArticle || fromTopicLanding) && {
                            vehicle_category: [articleCategory],
                          }),
                        })
                      }}
                    >
                      <span css={[activePage && tw`border-red-500 border-b-4`]}>
                        {item?.title}
                      </span>
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>
        </div>
      )}
    </>
  )
}
export default CarCareSubNav
